import { EOI_MEDAL_FIELDS, type EoiEventRow, type EoiMedalField, type EoiSportRow, type EoiView } from "@seagames/contracts";
import { Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { eoiRemoveImpact, invalidateEntries, putEoi } from "../../lib/api/entries";
import { sportHistory, strongNotPicked } from "../../lib/entries/medal-history";
import { toast } from "../../lib/ui/toast";
import { CheckIcon } from "../accreditation/icons";
import { EmptyCard } from "../common/empty-card";
import { joinList } from "../fnb/parts";
import { GenderTag } from "../shortlist/parts";
import { InfoIcon } from "../transport/icons";
import { Celebration, type Medal, MedalDisc, NullableStepper, Scoreboard } from "./board";
import { MedalIcon } from "../common/medal-icon";
import { QualifyingLine, QuotaChip, TypeChip } from "./event-facts";
import { HistoryStrip, StrongHint, TierBadge, useMedalHistory } from "./history";
import {
	type EoiDraft,
	type EoiDraftRow,
	type EoiEventDraft,
	type EoiEventField,
	ESTIMATE_MAX,
	blankEventDraft,
	eoiBodySports,
	eoiDirtyIds,
	eoiDraftFrom,
	eoiEventsOf,
	eoiRemovedIds,
	eoiRowAthletes,
	eoiRowDirty,
	eoiRowMedals,
	eoiTotals,
	medalCapReason,
	medalRoom,
	pickEntriesCap,
	pickMedalCap,
	setPickField,
} from "./model";
import { LeaveGuard, ReopenSheet } from "./parts";
import { EntrySummary, EoiReviewContent, ReviewSheet } from "./review-sheet";
import { SidePanel } from "./side-panel";

type Filter = "all" | "selected" | "unselected";
type SportField = "est_athletes_men" | "est_athletes_women" | "est_officials" | EoiMedalField;

const MEDAL_OF: Record<EoiMedalField, Medal> = { expected_gold: "gold", expected_silver: "silver", expected_bronze: "bronze" };
const MEDAL_WORD = { expected_gold: "eoi.gold", expected_silver: "eoi.silver", expected_bronze: "eoi.bronze" } as const;

/** The Undo toast of a bulk deselect. */
const UNDO_ID = "eoi-undo-bulk";

/**
 * Expression of interest: the games' sports as a grid of cards. The tick in a
 * card's corner adds / removes the sport; the card opens its side panel
 * (a bottom sheet on phones) with the sport's OPTIONAL estimates and its
 * events by discipline — pick specific events (none picked = interested in
 * all of them), each with optional expected entries and medals, capped live
 * by contracts' `eventMedalCap` (the database guard's rule). Every number may
 * stay blank ("not given", never 0). One Save sends the whole list
 * (`PUT /entries/eoi`; unchanged rows are the API's no-op); removing a sport
 * with entries by number asks `/entries/eoi/remove-impact` first. "Review and
 * submit" shows what will be locked (saving first when there are changes).
 * When the API says `can_edit` is false (submitted, window closed,
 * read-only) the page is the summary.
 */
export function EoiEditor({
	view,
	tickedOnly,
	onTickedOnly,
	canReopen,
	search,
}: {
	view: EoiView;
	tickedOnly: boolean;
	onTickedOnly: (v: boolean) => void;
	canReopen: boolean;
	search: { games_id?: string; org?: string };
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const [draft, setDraft] = useState<EoiDraft>(() => eoiDraftFrom(view.sports));
	const dirtyIds = useMemo(() => eoiDirtyIds(view.sports, draft), [view.sports, draft]);
	const dirty = dirtyIds.length > 0;
	const [saving, setSaving] = useState(false);
	const [dialog, setDialog] = useState<"review" | "reopen" | null>(null);
	const [removal, setRemoval] = useState<{ names: string[]; entries: number } | null>(null);
	const [party, setParty] = useState(false);
	const [query, setQuery] = useState("");
	const [unselectedOnly, setUnselectedOnly] = useState(false);
	const [openId, setOpenId] = useState<string | null>(null);
	const [editFinal, setEditFinal] = useState(false);
	const [flash, setFlash] = useState<string | null>(null);
	/** Open the review once the save in flight lands ("Save and review"). */
	const reviewAfterSave = useRef(false);
	// Admins only (the active role): the NOC's past SEA Games results per sport; null for everyone else.
	const hist = useMedalHistory(view.games_id, view.organization.code);
	const tiers = useMemo(
		() => (hist ? new Map(view.sports.map((s) => [s.sport_id, sportHistory(hist.data, s.code, hist.noc)])) : null),
		[hist, view.sports],
	);

	// A refetch (after a save, or a colleague's) re-seeds the draft unless there are unsaved changes.
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-seed on a new server answer only
	useEffect(() => {
		if (!dirty) setDraft(eoiDraftFrom(view.sports));
	}, [view.sports]);

	const locked = !view.can_edit || saving;
	const totals = eoiTotals(view.sports, draft);
	const scope = { games_id: view.games_id, organization_id: view.organization.id };
	const filter: Filter = unselectedOnly ? "unselected" : tickedOnly ? "selected" : "all";
	const isFinal = view.submission.state === "FINAL";

	const setRow = (sportId: string, patch: (row: EoiDraftRow) => Partial<EoiDraftRow>) =>
		setDraft((d) => {
			const before = d[sportId];
			return before ? { ...d, [sportId]: { ...before, ...patch(before) } } : d;
		});
	const dismissUndo = () => toast.dismiss(UNDO_ID);

	const write = async () => {
		dismissUndo();
		setSaving(true);
		try {
			const res = await putEoi({ ...scope, sports: eoiBodySports(view.sports, draft) });
			await invalidateEntries();
			if (res.ebn_entries_removed > 0)
				toast.success(t("save.removedEntries", { count: res.ebn_entries_removed, n: fmt(res.ebn_entries_removed) }));
			else toast.success(t("save.done"));
			if (reviewAfterSave.current) setDialog("review");
		} catch (err) {
			toast.error(err);
		} finally {
			reviewAfterSave.current = false;
			setSaving(false);
			setRemoval(null);
		}
	};

	const save = async (thenReview = false) => {
		reviewAfterSave.current = thenReview;
		const removed = eoiRemovedIds(view.sports, draft);
		const withEbn = removed.filter((id) => (view.sports.find((s) => s.sport_id === id)?.ebn_events ?? 0) > 0);
		if (withEbn.length === 0) return void write();
		// Ask the API what would cascade: the view's own counts may be stale.
		setSaving(true);
		try {
			const impact = await eoiRemoveImpact(scope, removed);
			setSaving(false);
			if (impact.ebn_events === 0) return void write();
			const names = removed.map((id) => view.sports.find((s) => s.sport_id === id)?.name ?? id);
			const entries = impact.items.reduce((n, i) => n + i.ebn_events, 0);
			setRemoval({ names, entries });
		} catch (err) {
			reviewAfterSave.current = false;
			setSaving(false);
			toast.error(err);
		}
	};

	const q = query.trim().toLocaleLowerCase();
	const shown = view.sports.filter((s) => {
		const sel = draft[s.sport_id]?.selected ?? false;
		if (filter === "selected" && !sel) return false;
		if (filter === "unselected" && sel) return false;
		return !q || s.name.toLocaleLowerCase().includes(q) || s.code.toLocaleLowerCase().includes(q);
	});

	/** Select / deselect every sport SHOWN (the search and filter narrow it). Deselect offers an Undo. */
	const bulk = (selected: boolean) => {
		if (locked) return;
		const ids = shown.filter((s) => (draft[s.sport_id]?.selected ?? false) !== selected).map((s) => s.sport_id);
		if (ids.length === 0) return;
		setDraft((d) => {
			const next = { ...d };
			for (const id of ids) {
				const row = next[id];
				if (row) next[id] = { ...row, selected };
			}
			return next;
		});
		if (!selected)
			toast.undoable({
				id: UNDO_ID,
				message: t("eoi.bulkDeselected", { count: ids.length, n: fmt(ids.length) }),
				undoLabel: t("eoi.undo"),
				onUndo: () =>
					setDraft((d) => {
						const next = { ...d };
						for (const id of ids) {
							const row = next[id];
							if (row) next[id] = { ...row, selected: true };
						}
						return next;
					}),
			});
		else dismissUndo();
	};

	// A hint chip shows its card: every filter cleared, scrolled to, briefly ringed. It never adds the sport.
	const showTile = (code: string) => {
		const s = view.sports.find((x) => x.code === code);
		if (!s) return;
		setQuery("");
		setUnselectedOnly(false);
		onTickedOnly(false);
		setFlash(s.sport_id);
		window.setTimeout(() => setFlash((cur) => (cur === s.sport_id ? null : cur)), 1800);
		requestAnimationFrame(() => {
			const el = document.getElementById(`eoi-tile-${s.sport_id}`);
			const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
			el?.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
			el?.focus({ preventScroll: true });
		});
	};
	const strong = hist
		? strongNotPicked(
				hist.data,
				hist.noc,
				view.sports.filter((s) => draft[s.sport_id]?.selected).map((s) => s.code),
				view.sports.map((s) => s.code),
			)
		: [];

	const openSport = openId ? view.sports.find((s) => s.sport_id === openId) : undefined;
	const openRow = openId ? draft[openId] : undefined;

	const dialogs = (
		<>
			<LeaveGuard dirtyCount={dirtyIds.length} />
			<Modal
				open={removal !== null}
				onClose={() => {
					reviewAfterSave.current = false;
					setRemoval(null);
				}}
				dismissible={!saving}
				size="sm"
				title={t("remove.title", { count: removal?.names.length ?? 1 })}
				footer={
					<ModalActions>
						<button
							type="button"
							className="btn btn-line"
							onClick={() => {
								reviewAfterSave.current = false;
								setRemoval(null);
							}}
							disabled={saving}
						>
							{t("remove.cancel")}
						</button>
						<button type="button" className="btn btn-error" onClick={() => void write()} disabled={saving}>
							{saving && <span className="loading loading-spinner loading-xs" />}
							{t("remove.confirm")}
						</button>
					</ModalActions>
				}
			>
				<p className="text-[0.875rem] text-gms-ink-2">
					{removal && (
						<>
							{t("remove.lead", { count: removal.names.length, n: fmt(removal.names.length), sports: joinList(removal.names) })}{" "}
							{t("remove.cascade", { count: removal.entries, n: fmt(removal.entries) })}
						</>
					)}
				</p>
			</Modal>
			<ReviewSheet
				open={dialog === "review"}
				phase="EOI"
				orgName={view.organization.name}
				gamesId={view.games_id}
				organizationId={view.organization.id}
				canSubmit={view.can_submit}
				onClose={() => setDialog(null)}
				onDone={() => setParty(true)}
			>
				<EoiReviewContent view={view} />
			</ReviewSheet>
			<ReopenSheet
				open={dialog === "reopen"}
				phase="EOI"
				gamesId={view.games_id}
				organizationId={view.organization.id}
				orgName={view.organization.name}
				onClose={() => setDialog(null)}
			/>
			{party && <Celebration phase="EOI" search={search} onClose={() => setParty(false)} />}
		</>
	);

	if (view.sports.length === 0) return <EmptyCard title={t("eoi.noSportsTitle")}>{t("eoi.noSportsHint")}</EmptyCard>;

	// Read-only, or submitted (an admin may still choose to change it): the summary.
	if (!view.can_edit || (isFinal && !editFinal && !dirty))
		return (
			<div className="flex min-w-0 flex-col gap-[0.875rem] pb-[1rem]">
				<EntrySummary
					phase="EOI"
					view={view}
					onEdit={view.can_edit ? () => setEditFinal(true) : undefined}
					onReopen={isFinal && canReopen ? () => setDialog("reopen") : undefined}
				>
					<EoiReviewContent view={view} />
				</EntrySummary>
				{dialogs}
			</div>
		);

	const selectedCount = totals.sports;
	const counts: Record<Filter, number> = { all: view.sports.length, selected: selectedCount, unselected: view.sports.length - selectedCount };

	return (
		<div className="flex min-w-0 flex-col gap-[0.75rem] pb-[1rem]">
			{hist && (
				<StrongHint
					strong={strong}
					nameOf={(code) => view.sports.find((s) => s.code === code)?.name ?? code}
					onShow={showTile}
				/>
			)}
			<div className="flex flex-wrap items-center gap-[0.5rem]">
				<label className="ent-search">
					<SearchGlyph />
					<input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("eoi.search")} aria-label={t("eoi.search")} />
				</label>
				<div className="ent-chips" role="group" aria-label={t("eoi.filterLabel")}>
					{(["all", "selected", "unselected"] as const).map((f) => (
						<button
							key={f}
							type="button"
							aria-pressed={filter === f}
							onClick={() => {
								setUnselectedOnly(f === "unselected");
								onTickedOnly(f === "selected");
							}}
						>
							{t(`eoi.filter.${f}`)}
							<span className="ent-chips__n">{fmt(counts[f])}</span>
						</button>
					))}
				</div>
				<div className="ent-bulk" role="group" aria-label={t("eoi.bulkLabel")}>
					<button type="button" className="btn btn-xs btn-line" onClick={() => bulk(true)} disabled={locked} title={t("eoi.selectAllHint")}>
						{t("eoi.selectAll")}
					</button>
					<button type="button" className="btn btn-xs btn-line" onClick={() => bulk(false)} disabled={locked} title={t("eoi.deselectAllHint")}>
						{t("eoi.deselectAll")}
					</button>
				</div>
			</div>

			<p className="flex items-center gap-[0.375rem] text-[0.75rem] text-gms-ink-3">
				<InfoIcon className="size-[0.875rem] shrink-0" aria-hidden />
				{t("eoi.toggleHint")}
			</p>

			{shown.length === 0 ? (
				<EmptyCard
					title={q ? t("eoi.noMatch", { q: query.trim() }) : t("eoi.emptyTitle")}
					actions={
						<button
							type="button"
							className="btn btn-sm btn-line"
							onClick={() => {
								setQuery("");
								setUnselectedOnly(false);
								onTickedOnly(false);
							}}
						>
							{t("eoi.showAll")}
						</button>
					}
				>
					{q ? undefined : t("eoi.emptyHint")}
				</EmptyCard>
			) : (
				<ul className="ent-tiles" aria-label={t("eoi.heading")}>
					{shown.map((s) => {
						const d = draft[s.sport_id];
						if (!d) return null;
						const picked = Object.keys(d.events).length;
						const counted = eoiRowAthletes(s, d);
						const athletes =
							counted.est_athletes_men === null && counted.est_athletes_women === null
								? null
								: (counted.est_athletes_men ?? 0) + (counted.est_athletes_women ?? 0);
						const tier = tiers?.get(s.sport_id);
						return (
							<li
								key={s.sport_id}
								className="ent-sc"
								data-on={d.selected || undefined}
								data-flash={flash === s.sport_id || undefined}
								data-dirty={eoiRowDirty(s, d) || undefined}
							>
								{/* Two sibling controls, never nested: the card opens the panel, the tick adds / removes. */}
								<button
									type="button"
									id={`eoi-tile-${s.sport_id}`}
									className="ent-sc__open"
									aria-haspopup="dialog"
									aria-label={t("eoi.openSport", { sport: s.name })}
									onClick={() => setOpenId(s.sport_id)}
								>
									<span className="ent-sc__code" aria-hidden>
										{s.code}
									</span>
									<span className="ent-sc__name">
										{s.name}
										{tier && <TierBadge history={tier} />}
									</span>
									<span className="ent-sc__line">
										{!d.selected
											? t("eoi.eventsCount", { count: s.events_total, n: fmt(s.events_total) })
											: picked > 0
												? t("eoi.eventsPicked", { count: s.events_total, picked: fmt(picked), n: fmt(s.events_total) })
												: t("eoi.allEvents")}
									</span>
									{d.selected && (
										<span className="ent-sc__sub">
											{athletes === null ? t("eoi.noEstimate") : t("eoi.athletesCount", { count: athletes, n: fmt(athletes) })}
										</span>
									)}
								</button>
								<label className="ent-sc__tick" title={d.selected ? t("eoi.tileRemove", { sport: s.name }) : t("eoi.tileAdd", { sport: s.name })}>
									<input
										type="checkbox"
										checked={d.selected}
										disabled={locked}
										aria-label={t("eoi.interestedIn", { sport: s.name })}
										onChange={(e) => {
											const on = e.target.checked;
											setRow(s.sport_id, () => ({ selected: on }));
										}}
									/>
									<CheckIcon className="ent-sc__check" aria-hidden />
								</label>
							</li>
						);
					})}
				</ul>
			)}

			<Scoreboard
				stats={[
					{ key: "sports", label: t("eoi.tiles.sports"), value: totals.sports },
					{ key: "events", label: t("eoi.tiles.events"), value: totals.events },
					{
						key: "athletes",
						label: t("eoi.tiles.athletes"),
						value: totals.est_athletes_men + totals.est_athletes_women,
						note: t("eoi.tiles.mw", { m: fmt(totals.est_athletes_men), w: fmt(totals.est_athletes_women) }),
					},
					{ key: "officials", label: t("eoi.tiles.officials"), value: totals.est_officials },
				]}
				medals={{ gold: totals.expected_gold, silver: totals.expected_silver, bronze: totals.expected_bronze }}
				pendingCount={dirtyIds.length}
				saving={saving}
				isFinal={isFinal}
				submission={view.submission}
				canSubmit={view.can_submit}
				submitDisabledReason={view.totals.sports === 0 ? t("bar.submitNeeds") : undefined}
				canReopen={isFinal && canReopen}
				onDiscard={() => {
					dismissUndo();
					setDraft(eoiDraftFrom(view.sports));
				}}
				onSave={() => void save()}
				onReview={() => setDialog("review")}
				onSaveAndReview={!isFinal ? () => void save(true) : undefined}
				onReopen={() => setDialog("reopen")}
			/>

			<SidePanel
				open={!!openSport && !!openRow}
				onClose={() => setOpenId(null)}
				title={
					openSport && (
						<>
							{openSport.name} <span className="font-display text-[0.9375rem] font-semibold tracking-wide text-gms-ink-3">{openSport.code}</span>
						</>
					)
				}
				subtitle={openSport && t("eoi.panel.subtitle", { count: openSport.events_total, n: fmt(openSport.events_total) })}
				headerExtra={
					openSport &&
					openRow && (
						<label className="ent-switch">
							<input
								type="checkbox"
								role="switch"
								className="toggle toggle-sm toggle-primary"
								checked={openRow.selected}
								disabled={locked}
								onChange={(e) => {
									const on = e.target.checked;
									setRow(openSport.sport_id, () => ({ selected: on }));
								}}
							/>
							<span>
								<b className="block text-[0.8125rem] text-gms-navy-ink">{t("eoi.panel.interested")}</b>
								<span className="text-[0.71875rem] text-gms-ink-3">{openRow.selected ? t("eoi.panel.interestedOn") : t("eoi.panel.interestedOff")}</span>
							</span>
						</label>
					)
				}
				footer={
					<div className="flex flex-wrap items-center justify-between gap-[0.5rem]">
						<span className="text-[0.75rem] text-gms-ink-3">{t("eoi.panel.saveNote")}</span>
						<button type="button" className="btn btn-sm btn-primary" onClick={() => setOpenId(null)}>
							{t("eoi.done")}
						</button>
					</div>
				}
			>
				{openSport && openRow && (
					<SportPanelBody
						sport={openSport}
						row={openRow}
						locked={locked}
						onRow={(patch) => setRow(openSport.sport_id, (r) => ({ ...patch(r), selected: true }))}
						history={hist && <HistoryStrip data={hist.data} noc={hist.noc} code={openSport.code} sportName={openSport.name} />}
					/>
				)}
			</SidePanel>

			{dialogs}
		</div>
	);
}

/** The panel's body: the sport's optional estimates, then its events by discipline. Every change ticks the sport. */
function SportPanelBody({
	sport,
	row,
	locked,
	onRow,
	history,
}: {
	sport: EoiSportRow;
	row: EoiDraftRow;
	locked: boolean;
	onRow: (patch: (row: EoiDraftRow) => Partial<EoiDraftRow>) => void;
	/** Admins only: the NOC's past results in this sport. */
	history?: ReactNode;
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const events = eoiEventsOf(sport);
	const picked = Object.keys(row.events).length;
	const medals = eoiRowMedals(row);
	const [clamped, setClamped] = useState<string | null>(null);

	const sportField = (f: SportField, label: string, aria: string, medal?: Medal) => (
		<div key={f} className="grid gap-[0.25rem]">
			<span className="flex items-center gap-[0.3125rem] text-[0.71875rem] font-semibold text-gms-ink-2">
				{medal && <MedalIcon medal={medal} />}
				{label}
			</span>
			<NullableStepper
				value={row[f]}
				max={ESTIMATE_MAX}
				locked={locked}
				label={t("eoi.valueLabel", { field: aria, sport: sport.name })}
				decLabel={t("stepper.decrease", { field: aria })}
				incLabel={t("stepper.increase", { field: aria })}
				onValue={(v) => onRow(() => ({ [f]: v }))}
			/>
		</div>
	);

	const setEvents = (fn: (ev: Record<string, EoiEventDraft>) => Record<string, EoiEventDraft>) => onRow((r) => ({ events: fn(r.events) }));
	const togglePick = (e: EoiEventRow, on: boolean) =>
		setEvents((ev) => {
			const next = { ...ev };
			if (on) next[e.event_id] = next[e.event_id] ?? blankEventDraft();
			else delete next[e.event_id];
			return next;
		});
	const setPick = (e: EoiEventRow, field: EoiEventField, v: number | null) => {
		const cur = row.events[e.event_id] ?? blankEventDraft();
		const res = setPickField(e, cur, field, v);
		setClamped(res.clamped ? e.event_id : null);
		setEvents((ev) => ({ ...ev, [e.event_id]: res.pick }));
	};
	const pickAll = (on: boolean) =>
		setEvents((ev) => {
			if (!on) return {};
			const next = { ...ev };
			for (const e of events) if (e.is_active || next[e.event_id]) next[e.event_id] = next[e.event_id] ?? blankEventDraft();
			return next;
		});

	return (
		<div className="grid gap-[1rem]">
			<section aria-labelledby={`eoi-est-${sport.sport_id}`} className="grid gap-[0.5rem]">
				<h3 id={`eoi-est-${sport.sport_id}`} className="ent-side__h">
					{t("eoi.panel.estimates")}
				</h3>
				<p className="text-[0.75rem] text-gms-ink-3">{t("eoi.panel.estimatesHint")}</p>
				<div className="flex flex-wrap gap-x-[0.875rem] gap-y-[0.625rem]">
					{sportField("est_athletes_men", t("eoi.men"), t("eoi.menAthletes"))}
					{sportField("est_athletes_women", t("eoi.women"), t("eoi.womenAthletes"))}
					{sportField("est_officials", t("eoi.officials"), t("eoi.officials"))}
				</div>
				{picked === 0 ? (
					<div className="grid gap-[0.375rem]">
						<span className="text-[0.75rem] font-semibold text-gms-navy-ink">{t("eoi.groupMedals")}</span>
						<div className="flex flex-wrap gap-x-[0.875rem] gap-y-[0.625rem]">
							{EOI_MEDAL_FIELDS.map((f) => sportField(f, t(MEDAL_WORD[f]), t(MEDAL_WORD[f]), MEDAL_OF[f]))}
						</div>
					</div>
				) : (
					<p className="flex flex-wrap items-center gap-[0.375rem] text-[0.75rem] text-gms-ink-2">
						<span className="font-semibold text-gms-navy-ink">{t("eoi.groupMedals")}</span>
						{EOI_MEDAL_FIELDS.map((f) => (
							<MedalDisc
								key={f}
								medal={MEDAL_OF[f]}
								value={medals[f] ?? 0}
								label={t("board.medalCount", { medal: t(MEDAL_WORD[f]), n: fmt(medals[f] ?? 0) })}
							/>
						))}
						<span className="text-gms-ink-3">{t("eoi.panel.medalsFromEvents")}</span>
					</p>
				)}
			</section>

			{history}

			<section aria-labelledby={`eoi-ev-${sport.sport_id}`} className="grid gap-[0.5rem]">
				<div className="flex flex-wrap items-center gap-[0.5rem]">
					<h3 id={`eoi-ev-${sport.sport_id}`} className="ent-side__h mr-auto">
						{t("eoi.panel.events")}
						<span className="ml-[0.375rem] font-normal text-gms-ink-3">
							{picked > 0 ? t("eoi.eventsPicked", { count: events.length, picked: fmt(picked), n: fmt(events.length) }) : t("eoi.allEvents")}
						</span>
					</h3>
					<button type="button" className="btn btn-xs btn-line" onClick={() => pickAll(true)} disabled={locked || events.length === 0}>
						{t("eoi.panel.selectAllEvents")}
					</button>
					<button type="button" className="btn btn-xs btn-line" onClick={() => pickAll(false)} disabled={locked || picked === 0}>
						{t("eoi.panel.deselectAllEvents")}
					</button>
				</div>
				<p className="ent-hint ent-hint--info">
					<InfoIcon aria-hidden className="mt-[0.0625rem] size-[0.875rem] shrink-0" />
					{t("eoi.panel.allEventsHint")}
				</p>
				{events.length === 0 ? (
					<p className="text-[0.78125rem] text-gms-ink-3">{t("eoi.panel.noEvents")}</p>
				) : (
					sport.disciplines.map((d) =>
						d.events.length === 0 ? null : (
							<div key={d.discipline_id} className="grid gap-[0.375rem]">
								{sport.disciplines.length > 1 && <h4 className="text-[0.75rem] font-bold text-gms-ink-2">{d.name}</h4>}
								<ul className="grid gap-[0.375rem]">
									{d.events.map((e) => (
										<EventPick
											key={e.event_id}
											event={e}
											pick={row.events[e.event_id]}
											locked={locked}
											clamped={clamped === e.event_id}
											onToggle={(on) => togglePick(e, on)}
											onField={(f, v) => setPick(e, f, v)}
											onClamp={() => setClamped(e.event_id)}
										/>
									))}
								</ul>
							</div>
						),
					)
				)}
				{sport.ebn_events > 0 && <p className="text-[0.75rem] text-gms-ink-3">{t("eoi.ebnNote", { count: sport.ebn_events, n: fmt(sport.ebn_events) })}</p>}
			</section>
		</div>
	);
}

/** One event of the panel: tick to pick; once picked, optional expected entries and medals, capped live. */
function EventPick({
	event: e,
	pick,
	locked,
	clamped,
	onToggle,
	onField,
	onClamp,
}: {
	event: EoiEventRow;
	pick: EoiEventDraft | undefined;
	locked: boolean;
	clamped: boolean;
	onToggle: (on: boolean) => void;
	onField: (f: EoiEventField, v: number | null) => void;
	/** A typed number was above the cap and was lowered. */
	onClamp: () => void;
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const on = !!pick;
	const capEntries = pickEntriesCap(e);
	return (
		<li className="ent-ev" data-on={on || undefined}>
			<label className="ent-ev__pick">
				<input type="checkbox" className="checkbox checkbox-sm" checked={on} disabled={locked} onChange={(ev) => onToggle(ev.target.checked)} />
				<span className="min-w-0 text-[0.8125rem] leading-tight font-semibold text-gms-navy-ink">{e.name}</span>
			</label>
			<div className="ent-ev__facts">
				<GenderTag gender={e.gender} />
				<TypeChip event={e} />
				<QuotaChip event={e} />
				<QualifyingLine event={e} />
			</div>
			{pick && (
				<div className="ent-ev__nums">
					<div className="grid gap-[0.25rem]">
						<span className="text-[0.71875rem] font-semibold text-gms-ink-2">{e.competitor_type === "TEAM" ? t("eoi.ev.teams") : t("eoi.ev.entries")}</span>
						<NullableStepper
							value={pick.est_entries}
							min={1}
							max={capEntries}
							locked={locked}
							label={t("eoi.ev.entriesLabel", { event: e.name })}
							decLabel={t("eoi.ev.less", { field: t("eoi.ev.entries"), event: e.name })}
							incLabel={t("eoi.ev.more", { field: t("eoi.ev.entries"), event: e.name })}
							maxTitle={t("eoi.ev.entriesMax", { count: capEntries, n: fmt(capEntries) })}
							onValue={(v) => onField("est_entries", v)}
							onClamp={onClamp}
						/>
					</div>
					{e.is_medal_event &&
						EOI_MEDAL_FIELDS.map((f) => {
							const word = t(MEDAL_WORD[f]);
							const room = medalRoom(e, pick, f);
							return (
								<div key={f} className="grid gap-[0.25rem]">
									<span className="flex items-center gap-[0.3125rem] text-[0.71875rem] font-semibold text-gms-ink-2">
										<MedalIcon medal={MEDAL_OF[f]} />
										{word}
									</span>
									<NullableStepper
										value={pick[f]}
										max={Math.max(room, pick[f] ?? 0)}
										locked={locked}
										label={t("eoi.ev.medalLabel", { medal: word, event: e.name })}
										decLabel={t("eoi.ev.less", { field: word, event: e.name })}
										incLabel={t("eoi.ev.more", { field: word, event: e.name })}
										maxTitle={t("eoi.cap.reached")}
										onValue={(v) => onField(f, v)}
										onClamp={onClamp}
									/>
								</div>
							);
						})}
					<MedalCapNote event={e} pick={pick} clamped={clamped} />
				</div>
			)}
		</li>
	);
}

/** Why the medal steppers stop where they do — the cap in words (contracts' `eventMedalCap`). */
function MedalCapNote({ event: e, pick, clamped }: { event: EoiEventRow; pick: EoiEventDraft; clamped: boolean }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const reason = medalCapReason(e, pick);
	const cap = pickMedalCap(e, pick);
	const text =
		reason === "none"
			? t("eoi.cap.none")
			: reason === "entries"
				? t("eoi.cap.entries", { count: cap.total, n: fmt(cap.total) })
				: reason === "quota"
					? t("eoi.cap.quota", { count: cap.total, n: fmt(cap.total) })
					: t("eoi.cap.offered", { g: fmt(e.gold_count), s: fmt(e.silver_count), b: fmt(e.bronze_count) });
	return (
		<p className="ent-ev__cap" aria-live="polite">
			<span>{text}</span>
			{e.is_medal_event && e.competitor_type === "TEAM" && <span>{t("eoi.cap.team")}</span>}
			{clamped && <b className="text-gms-warn">{t("eoi.cap.clamped")}</b>}
		</p>
	);
}

export function SearchGlyph() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden className="size-[0.9375rem] shrink-0 text-gms-ink-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
			<circle cx="11" cy="11" r="6.5" />
			<path d="m16 16 4.5 4.5" />
		</svg>
	);
}
